import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { AxiosError } from "axios";
import { bakeCookie, readCookie, deleteCookie } from "../../helpers/cookie-helper";
import { show as showSnackbar } from "../slices/snackbar";
import { toggle as toggleDialog } from "../slices/dialog";
import { stringToObject } from "../../helpers/object-helper";
import axiosServices from "../../utils/axios";

export interface UserState {
  id: string;
  orgId: string;
  username: string;
  fullname: string;
  roles: string[];
  accessToken: string;
  refreshToken: string;
  isLoggedin: boolean;
  expiresAt: number;
  staffCode: string;
  checkingStatus: "none" | "processing" | "done" | "failed"; 
}

const initialState: UserState = {
  id: "",
  orgId: "",
  username: "",
  fullname: "",
  accessToken: "",
  refreshToken: "",
  isLoggedin: false,
  checkingStatus: "none",
  expiresAt: 0,
  staffCode: "",
  roles: [],
};

export const userSlice = createSlice({
  name: "user",
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    check: (state: UserState) => {
      const cookieValue = readCookie('user');
      const user = stringToObject(cookieValue);
      if(user){
        if(user.expiresAt > (new Date()).getTime()){
          state.accessToken = user.accessToken;
          state.checkingStatus = 'done';
          state.expiresAt = user.expiresAt;
          state.fullname = user.fullname;
          state.id = user.id;
          state.isLoggedin = true;
          state.refreshToken = user.refreshToken;
          state.username = user.username;
          state.orgId = user.orgId;
          state.roles = user.roles || [];
          state.staffCode = user.staffCode;

          axiosServices.defaults.headers.common.Authorization = `Bearer ${user.accessToken}`;
        } else {
          state = {...initialState};
        }
      }else{
        state = {...initialState};
      }
    },
    logout: (state: UserState) => {
      deleteCookie('user');
      state.isLoggedin = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.checkingStatus = "none";
      })
      .addCase(login.fulfilled, (state, action) => {
        const now = new Date();
        state.id = action.payload.user.id;
        state.orgId = action.payload.user.orgId;
        state.username = action.payload.user.username;
        state.fullname = '';//action.payload.user.fullname;
        state.accessToken = action.payload.accessToken;
        state.refreshToken = action.payload.refreshToken;
        state.expiresAt = (new Date(now.getTime()+action.payload.expiresIn*1000)).getTime();
        state.isLoggedin = true;
        state.roles = action.payload.user.roles;
        state.staffCode = action.payload.user.staffCode;
        state.checkingStatus = "done";
        
        bakeCookie("user", state);
      })
      .addCase(login.rejected, (state) => {
        state.isLoggedin = false;
        state.checkingStatus = "failed";
      })
  },
});

// Actions

export const { check, logout } = userSlice.actions;

// ----------------------------------------------------------------------

export const login = createAsyncThunk(
  "user/login",
  async (loginObject: { username: string; password: string }, { dispatch }) => {
    const apiURL = `${process.env.REACT_APP_ID_API}/users/login`;
    var data = JSON.stringify(loginObject);
    const config = {
      method: "post",
      url: apiURL,
      headers: {
        "Content-Type": "application/json",
      },
      data: data,
    };
    const result = await axiosServices(config).then(res => res.data).catch(error => error);
    if(result instanceof AxiosError){
      const statusCode = (result.response ? result.response.status : '')
      switch (statusCode) {
        case 401:{
          dispatch(showSnackbar({ message: "Tài khoản đang bị khóa, vui lòng liên hệ người quản trị!", type: "error" }));
          break;
        }
        case 404:
        case 400:{
          dispatch(showSnackbar({ message: "Thông tin đăng nhập không hợp lệ!", type: "error" }));
          break;
        }
        default:{
          dispatch(showSnackbar({ message: "Đăng nhập không thành công!", type: "error" }));
          break;
        }
      }
      throw result;
    }else{
      dispatch(showSnackbar({ message: "Đăng nhập thành công!", type: "success" }));
      dispatch(toggleDialog());
      return result;
    }
  }
);


export default userSlice.reducer;
